import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useParams } from "react-router-dom";
import { Button } from "@/shared/components/shadcn/Button";
import { Card } from "@/shared/components/ui/Card";
import { CompactActions } from "@/shared/components/ui/CompactActions";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { DetailSummary } from "@/shared/components/ui/DetailSummary";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { CalendarEventDateTime } from "../components/CalendarEventDateTime";
import { useCalendarEvent, useDeleteCalendarEvent } from "../hooks/calendarEventQueries";
import { useCalendarEventOperation } from "../hooks/useCalendarEventOperation";

export function CalendarEventDetailsPage() {
  const { eventId = "" } = useParams();
  const { goToCalendarEventEdit, goToAppointments } = useAppNavigation();
  const query = useCalendarEvent(eventId);
  const remove = useDeleteCalendarEvent(eventId);
  const captureOperation = useCalendarEventOperation();
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string>();
  const event = query.data;
  if (query.isLoading || query.error || !event) return <Page><PageHeader title="Calendar event" /><PageContent>
    {query.error ? <ErrorState message={query.error.message} onRetry={() => void query.refetch()} /> : query.isLoading ? <RowSkeleton /> : <EmptyState message="Event not found." />}
  </PageContent></Page>;
  return (
    <Page>
      <PageHeader title={event.title || "Blocked time"} actions={<PageHeaderAction disabled={remove.isPending} onClick={() => goToCalendarEventEdit(event.id)}>Edit</PageHeaderAction>} />
      <PageContent>
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <CompactActions>
            <Button variant="destructive" aria-label="Delete event" disabled={remove.isPending} onClick={() => { setError(undefined); setDeleting(true); }}><Trash2 aria-hidden="true" />Delete</Button>
          </CompactActions>
          {error && !deleting && <ErrorState message={error} onRetry={() => { setError(undefined); void query.refetch(); }} />}
          <Section title="Details">
            <Card>
              <DetailSummary items={[
                { label: "Type", value: event.kind === "personal" ? "Personal event" : "Blocked time" },
                { label: "Availability", value: <StatusBadge tone={event.blocksAvailability ? "neutral" : "positive"}>{event.blocksAvailability ? "Busy" : "Free"}</StatusBadge> },
                { label: "Date & time", value: <CalendarEventDateTime {...event} /> },
              ]} />
            </Card>
          </Section>
          <Section title="Notes">
            <Card><p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{event.notes || "No notes"}</p></Card>
          </Section>
        </div>
      </PageContent>
      <ConfirmationDialog
        open={deleting} onOpenChange={setDeleting} title="Delete event?"
        description="Remove this event from your calendar. Existing appointments will not change."
        confirmLabel="Delete event" dismissLabel="Keep event" destructive pending={remove.isPending} error={error}
        onConfirm={() => {
          const isCurrent = captureOperation();
          void remove.mutateAsync(event.version).then(() => {
            if (isCurrent()) goToAppointments();
          }).catch((cause: unknown) => {
            if (!isCurrent()) return;
            setError(cause instanceof Error ? `${cause.message} Refresh the event before trying again.` : "Could not delete the event.");
            setDeleting(false);
          });
        }}
      />
    </Page>
  );
}
